import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Package, FileText, CalendarX2, Upload, Clock3, CheckCircle2, XCircle, Ban, Loader2, Trash2, Camera, X, FileDown, Inbox, MessageSquare } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { easerGet, easerPost, easerDelete, errorMessage, fmtShortDate, compressImage, getToken } from '../../lib/easerApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const REQUEST_TYPES = [
  { id: 'supply', label: 'Fournitures', hint: 'Gants, produits, tenue…', icon: Package },
  { id: 'document', label: 'Demande de document', hint: 'Attestation, bulletin, contrat…', icon: FileText },
  { id: 'unavailability', label: 'Indisponibilité', hint: 'Rendez-vous, empêchement…', icon: CalendarX2 },
  { id: 'upload', label: 'Envoyer un document', hint: 'Arrêt de travail, justificatif, RIB… (converti en PDF)', icon: Upload },
];
const STATUS = {
  pending: { label: 'En attente', icon: Clock3, className: 'bg-[hsl(var(--accent))] text-white' },
  approved: { label: 'Acceptée', icon: CheckCircle2, className: 'bg-[hsl(var(--brand-sage))] text-white' },
  done: { label: 'Traitée', icon: CheckCircle2, className: 'bg-[hsl(var(--primary))] text-white' },
  refused: { label: 'Refusée', icon: XCircle, className: 'bg-destructive text-destructive-foreground' },
  cancelled: { label: 'Annulée', icon: Ban, className: 'bg-muted text-muted-foreground' },
};
export function RequestStatusBadge({ status, doneLabel }) {
  const s = STATUS[status] || STATUS.pending;
  return <Badge className={`${s.className} hover:${s.className} gap-1`} data-testid="request-status"><s.icon className="h-3 w-3" /> {status === 'done' && doneLabel ? doneLabel : s.label}</Badge>;
}
export const requestFileUrl = (requestId, fileId) => `${BACKEND_URL}/api/easer/requests/${requestId}/files/${fileId}?token=${encodeURIComponent(getToken())}`;

const readAsDataUrl = (file) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => reject(new Error('Fichier illisible')); r.readAsDataURL(file); });

/** GRH > Demandes: supplies, document requests, unavailabilities and document uploads (converted to PDF), with status follow-up. */
export function EaserRequests({ onUnauthorized }) {
  const [items, setItems] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [creating, setCreating] = useState(null); // type id
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([easerGet('/api/easer/requests'), catalog ? Promise.resolve({ data: catalog }) : easerGet('/api/easer/requests/catalog')]);
      setItems(r.data.items);
      if (!catalog) setCatalog(c.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized?.();
      setItems((i) => i || []);
      toast.error(errorMessage(err));
    }
  }, [onUnauthorized, catalog]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (r) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    setBusyId(r.id);
    try {
      await easerDelete(`/api/easer/requests/${r.id}`);
      toast.success('Demande annulée');
      await load();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized?.();
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const visible = (items || []).filter((r) => filter === 'all' || (filter === 'open' ? ['pending', 'approved'].includes(r.status) : r.status === filter));

  return (
    <div className="space-y-4" data-testid="easer-requests">
      <div className="grid grid-cols-2 gap-2" data-testid="request-type-picker">
        {REQUEST_TYPES.map((t) => (
          <button key={t.id} type="button" onClick={() => setCreating(t.id)} data-testid={`request-new-${t.id}`}
            className="rounded-xl border border-border bg-card p-3 text-left min-h-[76px] hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors">
            <span className="flex items-center gap-2 font-semibold text-sm"><t.icon className="h-4 w-4 text-[hsl(var(--primary))]" /> {t.label}</span>
            <span className="block text-[11px] text-muted-foreground mt-1 leading-tight">{t.hint}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-muted-foreground">Mes demandes</h3>
        <div className="flex rounded-lg border border-border bg-card p-0.5">
          {[['all', 'Toutes'], ['open', 'En cours'], ['done', 'Traitées']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`requests-filter-${id}`} className={`px-2.5 h-8 rounded-md text-xs font-medium ${filter === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>{label}</button>
          ))}
        </div>
      </div>

      {items === null ? <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div> : visible.length === 0 ? (
        <Card className="p-8 text-center" data-testid="requests-empty">
          <Inbox className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Aucune demande</p>
          <p className="text-sm text-muted-foreground mt-1">Choisissez un type ci-dessus pour faire une demande à l'agence.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => {
            const t = REQUEST_TYPES.find((x) => x.id === r.type) || REQUEST_TYPES[0];
            return (
              <Card key={r.id} className="p-4 border-border shadow-sm" data-testid="request-item" data-type={r.type} data-status={r.status}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><t.icon className="h-3.5 w-3.5" /> {t.label} · {fmtShortDate(r.created_at)}</p>
                    <p className="font-semibold mt-0.5 leading-snug" data-testid="request-summary">{r.summary}</p>
                  </div>
                  <RequestStatusBadge status={r.status} doneLabel={r.done_label} />
                </div>
                {r.payload?.items?.length > 0 && (
                  <ul className="mt-2 text-sm text-muted-foreground list-disc pl-5">{r.payload.items.map((i) => <li key={i.id}>{i.qty} {i.unit} {i.label}</li>)}{r.payload.free_text && <li>{r.payload.free_text}</li>}</ul>
                )}
                {r.files?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="request-files">
                    {r.files.map((f) => (
                      <Button key={f.id} asChild size="sm" variant="outline" className="h-9"><a href={requestFileUrl(r.id, f.id)} target="_blank" rel="noopener noreferrer" data-testid="request-file-link"><FileDown className="h-4 w-4 mr-1" /> {f.filename}{f.pages ? ` · ${f.pages} p.` : ''}</a></Button>
                    ))}
                  </div>
                )}
                {r.admin_comment && (
                  <p className="mt-2 text-sm rounded-lg bg-[hsl(var(--secondary))]/70 p-2.5 flex items-start gap-2" data-testid="request-admin-comment"><MessageSquare className="h-4 w-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" /> <span><span className="text-muted-foreground">Réponse de l'agence :</span> {r.admin_comment}</span></p>
                )}
                {r.status === 'pending' && (
                  <div className="mt-3 flex justify-end">
                    <Button size="sm" variant="ghost" className="h-9 text-destructive hover:text-destructive" onClick={() => cancel(r)} disabled={busyId === r.id} data-testid="request-cancel">
                      {busyId === r.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />} Annuler
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <RequestDialog type={creating} catalog={catalog} onClose={() => setCreating(null)} onCreated={() => { setCreating(null); load(); }} onUnauthorized={onUnauthorized} />
    </div>
  );
}

function RequestDialog({ type, catalog, onClose, onCreated, onUnauthorized }) {
  const t = REQUEST_TYPES.find((x) => x.id === type);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (type) setForm({ items: {}, free_text: '', kind: '', details: '', files: [], start: '', end: '', all_day: true, start_time: '09:00', end_time: '12:00', reason: '' });
  }, [type]);

  const toggleItem = (id, on) => setForm((f) => { const items = { ...f.items }; if (on) items[id] = items[id] || 1; else delete items[id]; return { ...f, items }; });
  const setQty = (id, qty) => setForm((f) => ({ ...f, items: { ...f.items, [id]: Math.max(1, Math.min(99, Number(qty) || 1)) } }));

  const addFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (!list.length) return;
    if ((form.files?.length || 0) + list.length > 5) return toast.error('5 fichiers maximum par envoi');
    try {
      const out = [];
      for (const file of list) {
        if (file.type === 'application/pdf') {
          if (file.size > 10_000_000) throw new Error('PDF trop lourd (10 Mo max)');
          out.push({ name: file.name, data: await readAsDataUrl(file), isPdf: true }); // eslint-disable-line no-await-in-loop
        } else if (file.type.startsWith('image/')) {
          out.push({ name: file.name, data: await compressImage(file, 1800, 0.85), isPdf: false }); // eslint-disable-line no-await-in-loop
        } else {
          throw new Error('Formats acceptés : photos (JPG, PNG, WebP) ou PDF');
        }
      }
      set('files', [...(form.files || []), ...out]);
    } catch (err) {
      toast.error(err.message || 'Fichier illisible');
    }
  };

  const submit = async () => {
    const body = { type };
    if (type === 'supply') {
      body.items = Object.entries(form.items).map(([id, qty]) => { const c = catalog?.items?.find((x) => x.id === id); return { id, label: c?.label || id, unit: c?.unit || '', qty }; });
      body.free_text = form.free_text;
      if (!body.items.length && !form.free_text.trim()) return toast.error('Cochez au moins une fourniture ou décrivez votre besoin');
    } else if (type === 'document' || type === 'upload') {
      if (!form.kind) return toast.error('Choisissez le type de document');
      body.kind = form.kind; body.details = form.details;
      if (type === 'upload') {
        if (!form.files?.length) return toast.error('Ajoutez au moins une photo ou un PDF');
        body.files = form.files.map((f) => f.data);
      }
    } else {
      if (!form.start) return toast.error('Indiquez la date');
      body.start = form.start; body.end = form.end || form.start; body.all_day = form.all_day; body.reason = form.reason;
      if (!form.all_day) { body.start_time = form.start_time; body.end_time = form.end_time; }
    }
    setSaving(true);
    try {
      await easerPost('/api/easer/requests', body);
      toast.success(type === 'upload' ? 'Document envoyé à l\'agence (converti en PDF)' : 'Demande envoyée à l\'agence');
      onCreated();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized?.();
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!t) return null;
  const kinds = type === 'document' ? catalog?.document_kinds : catalog?.upload_kinds;
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="request-dialog" data-type={type}>
        <DialogHeader className="text-left">
          <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2"><t.icon className="h-5 w-5 text-[hsl(var(--primary))]" /> {t.label}</DialogTitle>
          <DialogDescription>{type === 'upload' ? 'Vos photos sont converties en un seul PDF transmis à l\'agence.' : 'L\'agence valide votre demande et vous répond ici.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {type === 'supply' && (
            <>
              <div className="space-y-2" data-testid="supply-catalog">
                {(catalog?.items || []).map((c) => {
                  const on = form.items?.[c.id] != null;
                  return (
                    <div key={c.id} className={`flex items-center gap-3 rounded-lg border p-2.5 ${on ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/5' : 'border-border'}`}>
                      <Checkbox checked={on} onCheckedChange={(v) => toggleItem(c.id, !!v)} id={`sup-${c.id}`} data-testid={`supply-item-${c.id}`} />
                      <label htmlFor={`sup-${c.id}`} className="flex-1 text-sm cursor-pointer">{c.label}</label>
                      {on && <div className="flex items-center gap-1"><Input type="number" min={1} max={99} value={form.items[c.id]} onChange={(e) => setQty(c.id, e.target.value)} className="w-16 h-9" aria-label={`Quantité ${c.label}`} data-testid={`supply-qty-${c.id}`} /><span className="text-xs text-muted-foreground">{c.unit}</span></div>}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-1">
                <Label htmlFor="sup-free">Autre besoin (facultatif)</Label>
                <Textarea id="sup-free" rows={2} value={form.free_text} onChange={(e) => set('free_text', e.target.value)} placeholder="Ex. un balai, un seau…" maxLength={1500} data-testid="supply-free-text" />
              </div>
            </>
          )}

          {(type === 'document' || type === 'upload') && (
            <>
              <div className="space-y-1">
                <Label>Type de document</Label>
                <Select value={form.kind} onValueChange={(v) => set('kind', v)}>
                  <SelectTrigger data-testid="request-kind"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{Object.entries(kinds || {}).map(([k, label]) => <SelectItem key={k} value={k} data-testid={`request-kind-${k}`}>{label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="req-details">{type === 'document' ? 'Précisions (période, usage…)' : 'Note pour l\'agence (facultatif)'}</Label>
                <Textarea id="req-details" rows={3} value={form.details} onChange={(e) => set('details', e.target.value)} placeholder={type === 'document' ? 'Ex. bulletin de septembre, attestation pour la CAF…' : 'Ex. arrêt du 20 au 24 octobre'} maxLength={1500} data-testid="request-details" />
              </div>
              {type === 'upload' && (
                <div className="space-y-2">
                  <Label>Photos ou PDF (5 max.)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {(form.files || []).map((f, i) => (
                      <div key={i} className="relative rounded-lg border border-border bg-secondary/40 aspect-[3/4] overflow-hidden flex items-center justify-center" data-testid="upload-preview">
                        {f.isPdf ? <FileText className="h-8 w-8 text-muted-foreground" /> : <img src={f.data} alt="" className="w-full h-full object-cover" />}
                        <button type="button" onClick={() => set('files', form.files.filter((_, j) => j !== i))} aria-label="Retirer" className="absolute top-1 right-1 h-7 w-7 rounded-full bg-background/90 border border-border flex items-center justify-center" data-testid="upload-remove"><X className="h-3.5 w-3.5" /></button>
                        <span className="absolute bottom-1 left-1 right-1 text-[10px] bg-background/90 rounded px-1 truncate">{f.isPdf ? f.name : `Page ${i + 1}`}</span>
                      </div>
                    ))}
                    {(form.files?.length || 0) < 5 && (
                      <button type="button" onClick={() => fileRef.current?.click()} className="rounded-lg border-2 border-dashed border-border aspect-[3/4] flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:border-[hsl(var(--primary))]/60" data-testid="upload-add">
                        <Camera className="h-6 w-6" /> Photo / PDF
                      </button>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={addFiles} data-testid="upload-input" />
                  <p className="text-xs text-muted-foreground">Prenez chaque page en photo : elles seront assemblées dans un PDF.</p>
                </div>
              )}
            </>
          )}

          {type === 'unavailability' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label htmlFor="un-start">Du</Label><Input id="un-start" type="date" value={form.start} onChange={(e) => set('start', e.target.value)} data-testid="unav-start" /></div>
                <div className="space-y-1"><Label htmlFor="un-end">Au (facultatif)</Label><Input id="un-end" type="date" value={form.end} min={form.start} onChange={(e) => set('end', e.target.value)} data-testid="unav-end" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={form.all_day} onCheckedChange={(v) => set('all_day', !!v)} data-testid="unav-all-day" /> Journée entière</label>
              {!form.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label htmlFor="un-st">De</Label><Input id="un-st" type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} data-testid="unav-start-time" /></div>
                  <div className="space-y-1"><Label htmlFor="un-et">À</Label><Input id="un-et" type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} data-testid="unav-end-time" /></div>
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="un-reason">Motif (facultatif)</Label>
                <Textarea id="un-reason" rows={2} value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="Ex. rendez-vous médical" maxLength={1000} data-testid="unav-reason" />
              </div>
              <p className="text-xs text-muted-foreground">Une fois validée par l'agence, l'indisponibilité apparaît dans votre planning et dans l'agenda de l'agence.</p>
            </>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" className="h-11" onClick={onClose} disabled={saving} data-testid="request-cancel-dialog">Annuler</Button>
          <Button className="h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" onClick={submit} disabled={saving} data-testid="request-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} {type === 'upload' ? 'Envoyer le document' : 'Envoyer la demande'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
